/** Display/unit metadata for a measure or dimension (see backend ``FormatSpec``).
 * Set only by a human or by accepting an ``LLMProposal`` (target_type
 * ``measure_format``/``dimension_format``) — never written directly by inference. */
export type SemanticFormatSpec = {
  unit_type?: string;
  unit_symbol?: string | null;
  decimal_places?: number | null;
  multiplier?: number;
  is_curated?: boolean;
};

/** How a move on this measure/metric is judged story-worthy (see backend
 * ``MaterialitySpec``). Same authoring contract as the format spec: written only by a
 * human or by accepting an ``LLMProposal`` (target_type
 * ``measure_materiality``/``metric_materiality``).
 *
 * ``basis: "auto"`` means "not declared" — it hands the decision back to the nugget
 * pipeline's own precedence chain and is NOT a rejection. Absent and ``auto`` mean the
 * same thing, so clearing the field is always safe. */
export type SemanticMaterialityBasis =
  | "auto"
  | "absolute"
  | "percent"
  | "share"
  | "range";

export type SemanticMaterialitySpec = {
  basis?: SemanticMaterialityBasis;
  min_abs_delta?: number | null;
  min_level?: number | null;
  min_percent_change?: number | null;
  min_share_of_total?: number | null;
  normal_low?: number | null;
  normal_high?: number | null;
  notes?: string | null;
  is_curated?: boolean;
};

export const MATERIALITY_BASIS_OPTIONS = [
  "auto",
  "absolute",
  "percent",
  "share",
  "range",
] as const satisfies readonly SemanticMaterialityBasis[];

/** Which fields each basis is described by. Mirrors the backend's
 * ``_MATERIALITY_BASIS_FIELDS`` — the form reveals only these, and only these are
 * submitted, so switching basis never sends a stale threshold from the previous one. */
export const MATERIALITY_BASIS_FIELDS = {
  auto: [],
  absolute: ["min_abs_delta", "min_level"],
  percent: ["min_percent_change"],
  share: ["min_share_of_total"],
  range: ["normal_low", "normal_high"],
} as const satisfies Record<
  SemanticMaterialityBasis,
  readonly (keyof SemanticMaterialitySpec)[]
>;

export function formatUnitSummary(format: SemanticFormatSpec | undefined): string {
  if (!format) {
    return "";
  }
  const unitType = (format.unit_type ?? "").trim();
  if (!unitType || unitType === "unknown") {
    return "";
  }
  const parts = [unitType];
  if (format.unit_symbol) {
    parts.push(format.unit_symbol);
  }
  if (typeof format.decimal_places === "number") {
    parts.push(`${format.decimal_places} decimal places`);
  }
  return parts.join(" · ");
}

function formatThreshold(value: number, symbol: string | null | undefined): string {
  const rendered = Number.isInteger(value)
    ? value.toLocaleString()
    : String(value);
  return symbol ? `${rendered} ${symbol}` : rendered;
}

function formatPercent(value: number): string {
  // Stored as a fraction; shown the way a person would say it.
  return `${Number((value * 100).toPrecision(4))}%`;
}

/** Plain-language echo of a materiality spec, so intent is confirmable without
 * reading the schema. Returns "" when nothing is declared. */
export function formatMaterialitySummary(
  materiality: SemanticMaterialitySpec | undefined,
  format?: SemanticFormatSpec | undefined,
): string {
  const basis = materiality?.basis ?? "auto";
  if (!materiality || basis === "auto") {
    return "";
  }
  const symbol = format?.unit_symbol ?? null;
  if (basis === "absolute") {
    const parts: string[] = [];
    if (typeof materiality.min_abs_delta === "number") {
      parts.push(`moves by more than ${formatThreshold(materiality.min_abs_delta, symbol)}`);
    }
    if (typeof materiality.min_level === "number") {
      parts.push(`sits above ${formatThreshold(materiality.min_level, symbol)}`);
    }
    return parts.length > 0 ? `Material when this ${parts.join(" and ")}` : "";
  }
  if (basis === "percent") {
    return typeof materiality.min_percent_change === "number"
      ? `Material when this moves by more than ${formatPercent(materiality.min_percent_change)}`
      : "";
  }
  if (basis === "share") {
    return typeof materiality.min_share_of_total === "number"
      ? `Material when this is at least ${formatPercent(materiality.min_share_of_total)} of the total`
      : "";
  }
  const low = materiality.normal_low;
  const high = materiality.normal_high;
  if (typeof low === "number" && typeof high === "number") {
    return `Material when this leaves the ${low}–${high} band`;
  }
  if (typeof low === "number") {
    return `Material when this falls below ${low}`;
  }
  if (typeof high === "number") {
    return `Material when this rises above ${high}`;
  }
  return "";
}
